//* Libraries
import React, { useMemo } from "react";
//* Import components from react-bootstrap
import { Table, Button, Spinner, Card } from "react-bootstrap";
//* Import icons from lucide-react
import {
  Info,
  CheckCircle,
  Clock,
  XCircle,
  File,
  AlertCircle,
  Users,
  Upload,
} from "lucide-react";
//* Import types
import { ImportHistoryCollaborator } from "../../../../hooks/importHistory/types/typesCollaborator";
//* Import CSVCollaborator type
import { CSVCollaborator } from "../../../../hooks/CSV/collaborators/useCSVCollaboratorData";

type ImportHistoryTableProps = {
  importHistory: ImportHistoryCollaborator[];
  isFetchingHistory: boolean;
  openErrorModal: (
    errors: {
      collaborator: CSVCollaborator;
      error: string;
      is_restore?: boolean;
      id: string;
    }[]
  ) => void;
};
export const ImportHistoryTable: React.FC<ImportHistoryTableProps> = ({
  importHistory,
  isFetchingHistory,
  openErrorModal,
}) => {
  const sortedImportHistory = useMemo(() => {
    return [...importHistory].sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }, [importHistory]);
  if (isFetchingHistory) {
    return (
      <Card className="border-0 shadow-sm h-100 d-flex align-items-center justify-content-center py-5">
        <div className="text-center">
          <Spinner
            animation="border"
            variant="primary"
            style={{ width: "2.5rem", height: "2.5rem", borderWidth: "0.25em" }}
          />
          <p className="mt-3 text-muted fw-light">
            Carregando histórico de importações...
          </p>
        </div>
      </Card>
    );
  }
  const getStatusBadge = (status: string) => {
    const baseClasses =
      "px-2 py-1 rounded-pill d-inline-flex align-items-center fw-medium";
    switch (status) {
      case "finished":
        return (
          <span
            className={`${baseClasses} bg-success bg-opacity-10 text-success`}
          >
            <CheckCircle size={14} className="me-1" />
            Finalizado
          </span>
        );
      case "processing":
        return (
          <span
            className={`${baseClasses} bg-warning bg-opacity-10 text-warning`}
          >
            <Clock size={14} className="me-1" />
            Processando
            <Spinner
              animation="border"
              size="sm"
              className="ms-2"
              style={{
                width: "0.8rem",
                height: "0.8rem",
                borderWidth: "0.1em",
              }}
            />
          </span>
        );
      default:
        return (
          <span
            className={`${baseClasses} bg-danger bg-opacity-10 text-danger`}
          >
            <XCircle size={14} className="me-1" />
            Erro
          </span>
        );
    }
  };
  const EmptyState = () => (
    <div className="text-center py-5">
      <div className="mb-3">
        <Upload size={48} className="text-primary opacity-25" />
      </div>
      <h6 className="text-muted fw-medium mb-2">
        Nenhuma importação encontrada
      </h6>
      <p className="text-muted small mb-0">
        As importações realizadas aparecerão aqui
      </p>
    </div>
  );
  return (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-white border-bottom py-3">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <File size={20} className="text-primary me-2" />
            <h6 className="mb-0 fw-semibold">Histórico de Importações</h6>
            {sortedImportHistory.length > 0 && (
              <span className="ms-2 px-2 py-1 badge bg-primary bg-opacity-10 text-primary rounded-pill fw-medium">
                {sortedImportHistory.length} importações
              </span>
            )}
          </div>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {sortedImportHistory.length > 0 ? (
          <div
            className="table-responsive"
            style={{
              maxHeight: "600px",
              overflowY: "auto",
              scrollbarWidth: "thin",
            }}
          >
            <Table hover className="mb-0 table-borderless">
              <thead className="bg-light text-muted">
                <tr>
                  <th className="px-3 py-3 border-bottom">Data</th>
                  <th className="px-3 py-3 border-bottom">Status</th>
                  <th className="px-3 py-3 border-bottom">Arquivo</th>
                  <th className="px-3 py-3 border-bottom">Erro</th>
                  <th className="px-3 py-3 text-center border-bottom">
                    <span className="d-flex align-items-center justify-content-center text-success">
                      <CheckCircle size={14} className="me-1" />
                      Sucesso
                    </span>
                  </th>
                  <th className="px-3 py-3 text-center border-bottom">
                    <span className="d-flex align-items-center justify-content-center text-danger">
                      <AlertCircle size={14} className="me-1" />
                      Erros
                    </span>
                  </th>
                  <th className="px-3 py-3 text-center border-bottom">
                    <span className="d-flex align-items-center justify-content-center text-primary">
                      <Users size={14} className="me-1" />
                      Total
                    </span>
                  </th>
                  <th className="px-3 py-3 text-center border-bottom">Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedImportHistory.map((item, index) => (
                  <tr
                    key={item.id}
                    className={index % 2 === 0 ? "bg-light bg-opacity-50" : ""}
                  >
                    <td className="px-3 py-3">
                      <div className="small fw-medium">
                        {new Date(item.timestamp).toLocaleDateString("pt-BR")}
                      </div>
                      <div className="text-muted small">
                        {new Date(item.timestamp).toLocaleTimeString("pt-BR")}
                      </div>
                    </td>
                    <td className="px-3 py-3">{getStatusBadge(item.status)}</td>
                    <td className="px-3 py-3">
                      <span
                        className="d-flex align-items-center text-truncate"
                        style={{ maxWidth: "200px" }}
                      >
                        <File
                          size={16}
                          className="me-2 text-primary opacity-75"
                        />
                        <span className="small fw-medium">
                          {item.file_name}
                        </span>
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      {item.error ? (
                        <span className="text-danger small d-flex align-items-center">
                          <AlertCircle size={14} className="me-1" />
                          {item.error}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="badge bg-success bg-opacity-10 text-success px-2 py-1">
                        {item.success}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="badge bg-danger bg-opacity-10 text-danger px-2 py-1">
                        {item.detailed_errors?.length || 0}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <span className="badge bg-primary bg-opacity-10 text-primary px-2 py-1">
                        {item.total}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      {item.detailed_errors &&
                      item.detailed_errors.length > 0 ? (
                        <Button
                          variant="link"
                          size="sm"
                          className="text-primary text-decoration-none d-inline-flex align-items-center p-0"
                          onClick={() =>
                            openErrorModal(
                              item.detailed_errors.map((error) => ({
                                collaborator: error.collaborator,
                                error: error.error,
                                is_restore: error.collaborator.is_restore,
                                id: error.id,
                              }))
                            )
                          }
                        >
                          <Info size={14} className="me-1" />
                          Ver Erros
                        </Button>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <EmptyState />
        )}
      </Card.Body>
    </Card>
  );
};
export default ImportHistoryTable;
